import type { LucideIcon } from "lucide-react"
import type { ReactNode, Ref } from "react"
import { forwardRef } from "react"
import { cn } from "@/lib/utils"

export const menuNavRowPaddingX = "px-3"
export const menuNavRowPaddingY = "py-2.5 min-h-[2.75rem]"
export const menuNavRowPaddingCompactY = "py-1.5 min-h-[2.25rem]"
export const menuNavIconSlotClass = "w-5 h-5 shrink-0 flex items-center justify-center"
export const menuNavBorderClass = "border-l-2"
export const menuNavActiveClasses =
  "border-green-600 bg-green-50/70 dark:border-green-400 dark:bg-green-950/40"
export const menuNavActiveIconClasses = "text-green-600 dark:text-green-300"
export const menuNavIdleIconClasses =
  "text-gray-500 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400"
export const menuNavBlueActiveClasses =
  "border-blue-600 bg-blue-50/70 dark:border-blue-400 dark:bg-blue-950/40"
export const menuNavBlueActiveIconClasses = "text-blue-600 dark:text-blue-300"
export const menuNavBlueIdleIconClasses =
  "text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400"
export const menuNavBlueHoverClasses = "hover:bg-blue-50/90 dark:hover:bg-blue-900/35"

type MenuNavAccent = "green" | "blue"

function menuNavActiveClassesFor(accent: MenuNavAccent): string {
  return accent === "blue" ? menuNavBlueActiveClasses : menuNavActiveClasses
}

function menuNavActiveIconClassesFor(accent: MenuNavAccent): string {
  return accent === "blue" ? menuNavBlueActiveIconClasses : menuNavActiveIconClasses
}

function menuNavIdleIconClassesFor(accent: MenuNavAccent): string {
  return accent === "blue" ? menuNavBlueIdleIconClasses : menuNavIdleIconClasses
}

function menuNavHoverClassesFor(accent: MenuNavAccent): string {
  return accent === "blue" ? menuNavBlueHoverClasses : "hover:bg-gray-50 dark:hover:bg-gray-700/80"
}

export function MenuSectionHeaderTile({
  Icon,
  title,
  subtitle,
  right,
  onClick,
  tileClassName,
  iconClassName,
  className,
  buttonRef,
  active,
}: {
  Icon: LucideIcon
  title: string
  subtitle?: string
  right?: ReactNode
  onClick?: () => void
  tileClassName: string
  iconClassName: string
  className?: string
  buttonRef?: Ref<HTMLButtonElement>
  active?: boolean
}) {
  const shellClassName = cn(
    "flex flex-wrap items-center gap-x-3 gap-y-1 w-full text-left rounded-lg py-2",
    menuNavRowPaddingX,
    menuNavBorderClass,
    active ? menuNavActiveClasses : "border-transparent",
    className
  )

  const inner = (
    <>
      <div className="flex items-center gap-3 grow basis-auto">
        <div className={cn(menuNavIconSlotClass, "rounded-md shrink-0", tileClassName)}>
          <Icon className={cn("w-4 h-4", iconClassName)} />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>
          {subtitle ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
          ) : null}
        </div>
      </div>
      {right ? (
        <div className="flex items-center gap-0.5 shrink-0 ml-auto">{right}</div>
      ) : null}
    </>
  )

  if (onClick) {
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={onClick}
        className={cn(
          shellClassName,
          "transition-colors hover:bg-black/[0.03] dark:hover:bg-white/5"
        )}
      >
        {inner}
      </button>
    )
  }

  return <div className={shellClassName}>{inner}</div>
}

export type MenuNavRowLeafProps = {
  Icon: LucideIcon
  label: string
  description?: string
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title?: string
  compact?: boolean
  right?: ReactNode
  className?: string
  accent?: MenuNavAccent
}

export const MenuNavRowLeaf = forwardRef<HTMLButtonElement, MenuNavRowLeafProps>(
  function MenuNavRowLeaf(
    {
      Icon,
      label,
      description,
      onClick,
      active,
      disabled,
      title,
      compact,
      right,
      className,
      accent = "green",
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        title={title}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "flex items-center gap-3 w-full text-left rounded-md transition-colors group",
          menuNavRowPaddingX,
          compact ? menuNavRowPaddingCompactY : menuNavRowPaddingY,
          menuNavBorderClass,
          active ? menuNavActiveClassesFor(accent) : "border-transparent",
          disabled ? "opacity-60 cursor-not-allowed" : menuNavHoverClassesFor(accent),
          className
        )}
      >
        <span className={menuNavIconSlotClass}>
          <Icon
            className={cn(
              "w-4 h-4 shrink-0",
              active ? menuNavActiveIconClassesFor(accent) : menuNavIdleIconClassesFor(accent)
            )}
          />
        </span>
        <div className="min-w-0 flex-1">
          <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
          {description ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">{description}</div>
          ) : null}
        </div>
        {right}
      </button>
    )
  }
)

MenuNavRowLeaf.displayName = "MenuNavRowLeaf"

export function sectionTintClasses(workspace: boolean, collections: boolean, admin: boolean) {
  if (workspace) {
    return "rounded-xl py-3 bg-slate-50/90 dark:bg-slate-900/45"
  }
  if (collections) {
    return "rounded-xl py-3 bg-blue-50/70 dark:bg-blue-950/25"
  }
  if (admin) {
    return "rounded-xl py-3 bg-orange-50/65 dark:bg-orange-950/25"
  }
  return ""
}
